import { useTimeoutFn } from '@vueuse/core';
import { onMounted } from 'vue';

type ToastTimerOptions = {
  durationMs: () => number;
  shouldPersist: () => boolean;
  onDismiss: () => void;
};

export function useToastTimer({
  durationMs,
  shouldPersist,
  onDismiss,
}: ToastTimerOptions) {
  let remainingMs = 0;
  let startedAt = 0;
  let isHovered = false;
  let isFocused = false;

  const { isPending, start, stop } = useTimeoutFn(dismiss, () => remainingMs, {
    immediate: false,
  });

  function startTimer() {
    if (shouldPersist()) return;
    remainingMs = durationMs();
    startedAt = Date.now();
    start();
  }
  function pauseTimer() {
    if (!isPending.value) return;
    stop();
    remainingMs -= Date.now() - startedAt;
  }
  function resumeTimer() {
    if (shouldPersist() || isPending.value || isHovered || isFocused) return;
    if (remainingMs <= 0) {
      dismiss();
      return;
    }
    startedAt = Date.now();
    start();
  }
  function handlePointerEnter() {
    isHovered = true;
    pauseTimer();
  }
  function handlePointerLeave() {
    isHovered = false;
    resumeTimer();
  }
  function handleFocusIn() {
    isFocused = true;
    pauseTimer();
  }
  function handleFocusOut() {
    isFocused = false;
    resumeTimer();
  }
  function dismiss() {
    stop();
    onDismiss();
  }

  onMounted(startTimer);

  return {
    handlePointerEnter,
    handlePointerLeave,
    handleFocusIn,
    handleFocusOut,
    dismiss,
  };
}
